<template lang="pug">
  .b-admin-supplier-form(data-testid="admin-supplier-form")
    ._title {{ supplier ? 'Правка поставщика' : 'Новый поставщик' }}
    ._grid
      ._field
        ._label E-mail учётной записи
        BaseInput(
          inputTestId="admin-supplier-form-user-email"
          v-model="form.userEmail"
          :disabled="!!supplier"
          )
      ._field(v-if="!supplier")
        ._label Пароль
        BaseInput(inputTestId="admin-supplier-form-password" v-model="form.password")
      ._field
        ._label Название
        BaseInput(inputTestId="admin-supplier-form-name" v-model="form.name")
      ._field
        ._label Регион
        BaseInput(inputTestId="admin-supplier-form-region" v-model="form.region")
      ._field
        ._label Адрес
        BaseInput(inputTestId="admin-supplier-form-address" v-model="form.address")
      ._field
        ._label Сайт
        BaseInput(inputTestId="admin-supplier-form-website" v-model="form.website")
      ._field
        ._label Телефон
        BaseInput(inputTestId="admin-supplier-form-phone" v-model="form.phone")
      ._field
        ._label E-mail карточки
        BaseInput(inputTestId="admin-supplier-form-email" v-model="form.email")
      ._field
        ._label Контактное лицо
        BaseInput(inputTestId="admin-supplier-form-contact-person" v-model="form.contactPerson")
      ._field.wide
        ._label Бренды (через запятую)
        BaseInput(inputTestId="admin-supplier-form-brands" v-model="brands")
      ._field.wide
        ._label Категории
        ._categories
          label._category(
            v-for="(title, code) in CATEGORIES"
            :key="code"
            data-testid="admin-supplier-form-category"
            )
            input._check(
              type="checkbox"
              data-testid="admin-supplier-form-category-checkbox"
              :checked="form.categories.includes(code)"
              @change="toggleCategory(code, $event.target.checked)"
              )
            span._category-name(data-testid="admin-supplier-form-category-name") {{ title }}
    ._error(v-if="error") {{ error }}
    ._buttons
      Btn(blue data-testid="admin-supplier-form-save" @click="submit") Сохранить
      Btn(grey data-testid="admin-supplier-form-cancel" @click="$emit('finish', false)") Отмена
</template>

<script>
import BaseInput from '@/components/BaseInput.vue';
import { CATEGORIES } from '@/utils/spec';

/**
 * Форма создания и правки поставщика в реестре администратора (ТЗ 6.7): e-mail и пароль учётной
 * записи (пароль только при создании), поля карточки, категории мультивыбором из восьми (2.3),
 * бренды тегами через запятую. Удаления и блокировки нет (У36), поэтому кнопки удаления в форме
 * тоже нет.
 *
 * Сохранять отправляет страница: реестр её, ей же перечитывать список. Сгенерировано Claude.
 */
export default {
  name: 'AdminSupplierFormPopupWrappable',
  components: { BaseInput },
  data() {
    return {
      CATEGORIES,
      supplier: null,
      brands: '',
      error: '',
      form: {
        userEmail: '',
        password: '',
        name: '',
        region: '',
        address: '',
        website: '',
        phone: '',
        email: '',
        contactPerson: '',
        categories: [],
      },
    };
  },
  methods: {
    init(params = {}) {
      const supplier = params.supplier ?? null;
      this.supplier = supplier;
      if (!supplier) return;

      this.form = {
        userEmail: supplier.userEmail ?? '',
        password: '',
        name: supplier.name ?? '',
        region: supplier.region ?? '',
        address: supplier.address ?? '',
        website: supplier.website ?? '',
        phone: supplier.phone ?? '',
        email: supplier.email ?? '',
        contactPerson: supplier.contactPerson ?? '',
        categories: [...(supplier.categories ?? [])],
      };
      this.brands = (supplier.brands ?? []).join(', ');
    },
    toggleCategory(code, checked) {
      const categories = this.form.categories.filter((item) => item !== code);
      if (checked) categories.push(code);
      this.form.categories = categories;
    },
    submit() {
      if (!this.form.name.trim()) {
        this.error = 'Укажите название поставщика';
        return;
      }
      if (!this.form.categories.length) {
        this.error = 'Выберите хотя бы одну категорию';
        return;
      }
      if (!this.supplier && (!this.form.userEmail.trim() || !this.form.password)) {
        this.error = 'Для новой учётной записи нужны e-mail и пароль';
        return;
      }
      this.error = '';

      const params = {
        name: this.form.name.trim(),
        region: this.form.region.trim() || null,
        address: this.form.address.trim() || null,
        website: this.form.website.trim() || null,
        phone: this.form.phone.trim() || null,
        email: this.form.email.trim() || null,
        contactPerson: this.form.contactPerson.trim() || null,
        categories: this.form.categories,
        brands: this.brands.split(',').map((tag) => tag.trim()).filter((tag) => !!tag),
      };

      if (!this.supplier) {
        params.userEmail = this.form.userEmail.trim();
        params.password = this.form.password;
      }

      this.$emit('finish', { supplier: this.supplier, params });
    },
  },
};
</script>

<style scoped lang="scss">
.admin-supplier-form {
  width: 640px;
  max-width: 100%;
  box-sizing: border-box;

  &__title {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 16px;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__field {
    flex: 0 0 calc(50% - 6px);
    box-sizing: border-box;

    &.wide {
      flex-basis: 100%;
    }
  }

  &__label {
    display: block;
    font-size: 12px;
    color: var(--color-middle-gray);
    margin-bottom: 4px;
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }

  &__category {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    cursor: pointer;
  }

  &__check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__error {
    margin-top: 12px;
    color: var(--color-red);
  }

  &__buttons {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }
}
</style>
